import { useEffect, useState, type CSSProperties } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "../../hooks";
import {
    getLastApiRequest,
    isDebugBarEnv,
    subscribeLastApiRequest,
    type LastApiRequestSnapshot,
} from "../../debug/last-api-request";
import { getPlatform } from "../../platform/platform";
import {
    getWebViewDebugSnapshot,
    subscribeWebViewDebug,
    type WebViewDebugSnapshot,
} from "../../debug/webview-debug";

const LINE_PX = 14;
const PADDING_Y = 8;

const rowStyle: CSSProperties = {
    lineHeight: `${LINE_PX}px`,
    height: `${LINE_PX}px`,
    fontSize: 11,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
};

const formatApiLine = (api: LastApiRequestSnapshot): string => {
    const status = api.error ? `err ${api.error}` : api.status != null ? String(api.status) : "…";
    return `api ${api.method} ${status} ${api.url}`;
};

export const DebugBar = () => {
    const location = useLocation();
    const yclid = useSelector(store => store.appData.yclid);
    const [webview, setWebview] = useState<WebViewDebugSnapshot>(getWebViewDebugSnapshot);
    const [api, setApi] = useState(getLastApiRequest);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        if (!isDebugBarEnv()) return;

        const refreshWebview = () => setWebview(getWebViewDebugSnapshot());
        refreshWebview();

        const unsubWebview = subscribeWebViewDebug(refreshWebview);
        const unsubApi = subscribeLastApiRequest(() => setApi(getLastApiRequest()));

        return () => {
            unsubWebview();
            unsubApi();
        };
    }, []);

    if (!isDebugBarEnv() || dismissed) {
        return null;
    }

    const statusMark = webview.valid ? "✔" : webview.tested ? "✖" : "…";
    const webviewErrors = webview.errors.filter((e) => /webview/i.test(e));
    const lines: { text: string; title?: string; warn?: boolean }[] = [
        { text: `webview: ${webview.versionLabel} ${statusMark}` },
        { text: `route ${location.pathname}${location.search}` },
        { text: `app platform ${getPlatform()}` },
    ];

    if (api) {
        lines.push({ text: formatApiLine(api), title: api.url });
    }

    lines.push({
        text: `yclid ${yclid || "—"}`,
        title: yclid || "yclid не получен",
        warn: !yclid,
    });

    if (webviewErrors.length > 0) {
        lines.push({
            text: webviewErrors.join(" | "),
            title: webviewErrors.join(" | "),
            warn: true,
        });
    }

    const lineCount = lines.length;

    return (
        <div
            style={{
                position: "fixed",
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 2147483000,
                padding: `${PADDING_Y}px 22px ${PADDING_Y}px 10px`,
                height: `${LINE_PX * lineCount + PADDING_Y * 2}px`,
                boxSizing: "border-box",
                background: "rgba(0,0,0,0.75)",
                color: "#fff",
                fontFamily:
                    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                borderTop: "1px solid rgba(255,255,255,0.12)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
            }}
        >
            <button
                type="button"
                aria-label="Закрыть панель отладки"
                onClick={() => setDismissed(true)}
                style={{
                    position: "absolute",
                    top: 2,
                    right: 4,
                    width: 16,
                    height: 16,
                    padding: 0,
                    border: "none",
                    borderRadius: 3,
                    background: "rgba(255,255,255,0.12)",
                    color: "rgba(255,255,255,0.85)",
                    fontSize: 12,
                    lineHeight: "14px",
                    cursor: "pointer",
                }}
            >
                ×
            </button>
            {lines.map((line, i) => (
                <div
                    key={`${i}-${line.text}`}
                    style={{
                        ...rowStyle,
                        color: line.warn ? "#ff9e80" : "#fff",
                    }}
                    title={line.title}
                >
                    {line.text}
                </div>
            ))}
        </div>
    );
};
